// /admin/people/groups/new and /admin/people/groups/:groupId/edit — minimal
// admin form for groups. Schema-equivalent to SCIM PATCH /Groups/{id}: every
// member is a Memberships row keyed by (TENANT#<tid>#GROUP#<gid>, MEMBER#<alias>)
// with groupName denormalized. Audit `source` auto-tags as "admin".

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { groupClient } from "@/data/clients/groupClient";
import { userClient } from "@/data/clients/userClient";
import "../AdminShared.css";

export function GroupFormPage(): JSX.Element {
  const { groupId: editGroupId } = useParams<{ groupId: string }>();
  const isEdit = Boolean(editGroupId);
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();

  const [groupId, setGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const userList = await userClient.list();
        if (cancelled) return;
        setAllUsers(userList.items.map((u) => u.alias).sort());

        if (isEdit && editGroupId) {
          const g = await groupClient.get(editGroupId);
          if (cancelled) return;
          setGroupId(g.group_id);
          setGroupName(g.group_name);
          setSelectedMembers(new Set(g.members.map((m) => m.alias)));
        }
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, { actionLabel: "Load group form" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editGroupId]);

  const toggleMember = useCallback((alias: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) {
        next.delete(alias);
      } else {
        next.add(alias);
      }
      return next;
    });
  }, []);

  const memberArray = useMemo(() => Array.from(selectedMembers), [selectedMembers]);

  const canSave =
    groupId.trim().length > 0 &&
    groupName.trim().length > 0 &&
    (isEdit || selectedMembers.size > 0) &&
    !submitting;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      if (isEdit && editGroupId) {
        // Edit flow: rename if changed, then diff membership.
        const existing = await groupClient.get(editGroupId);
        if (existing.group_name !== groupName.trim()) {
          await groupClient.rename(editGroupId, groupName.trim());
        }
        const beforeMembers = new Set(existing.members.map((m) => m.alias));
        const toAdd = memberArray.filter((a) => !beforeMembers.has(a));
        const toRemove = Array.from(beforeMembers).filter((a) => !selectedMembers.has(a));
        for (const alias of toAdd) {
          await groupClient.addMember(editGroupId, alias);
        }
        for (const alias of toRemove) {
          await groupClient.removeMember(editGroupId, alias);
        }
        toast.success({
          title: `Group '${groupName.trim()}' saved`,
          description: `+${toAdd.length} / -${toRemove.length} members`,
        });
      } else {
        await groupClient.create({
          group_id: groupId.trim(),
          group_name: groupName.trim(),
          members: memberArray,
        });
        toast.success({ title: `Group '${groupName.trim()}' created` });
      }
      navigate("/admin/people");
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Save group" });
      }
    } finally {
      setSubmitting(false);
    }
  }, [canSave, isEdit, editGroupId, groupId, groupName, memberArray, selectedMembers, toast, handleApiError, navigate]);

  if (loading) {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">← Groups &amp; users</Link>
          </div>
          <h1>Loading…</h1>
        </header>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/people">← Groups &amp; users</Link>
        </div>
        <h1>{isEdit ? `Edit group — ${groupName}` : "New group"}</h1>
        <div className="caption">
          Writes the same membership rows as your IdP&apos;s SCIM provisioner.
          The audit log tags every row as a UI change.
        </div>
      </header>

      <div
        style={{
          maxWidth: 720,
          marginTop: 16,
          padding: 20,
          borderRadius: 8,
          border: "1px solid var(--ring-border, #2a2d3a)",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="group-id" style={{ display: "block", fontWeight: 500, fontSize: 13, marginBottom: 6 }}>
            Group ID
          </label>
          <input
            id="group-id"
            type="text"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. site-managers-iad12"
            style={{ width: "100%" }}
          />
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--ring-neutral-300)" }}>
            Stable identifier — match what SCIM would emit (e.g. {`amzn1.abacus.team.…`}).
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="group-name" style={{ display: "block", fontWeight: 500, fontSize: 13, marginBottom: 6 }}>
            Display name
          </label>
          <input
            id="group-name"
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. WWOS Site Managers"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "block", fontWeight: 500, fontSize: 13, marginBottom: 6 }}>
            Members{" "}
            <span style={{ color: "var(--ring-neutral-300)", fontWeight: 400 }}>
              ({selectedMembers.size} selected)
            </span>
          </div>
          {allUsers.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--ring-neutral-300)", padding: 12 }}>
              No users yet. Create one first.
            </div>
          ) : (
            <div
              style={{
                maxHeight: 260,
                overflowY: "auto",
                border: "1px solid var(--ring-border, #2a2d3a)",
                borderRadius: 6,
                padding: 8,
              }}
            >
              {allUsers.map((alias) => (
                <label
                  key={alias}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.has(alias)}
                    onChange={() => toggleMember(alias)}
                  />
                  <span>{alias}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 24, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="v6-btn ghost"
            onClick={() => navigate("/admin/people")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="v6-btn primary"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {isEdit ? "Save changes" : "Create group"}
          </button>
        </div>
      </div>
    </section>
  );
}
